import { useEffect, useState } from "react";
import { FaPhoneAlt } from "react-icons/fa";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { contactInfo } from "../../data/contactInfo";
import styles from "./Header.module.css";

const resourceItems = [
  {
    label: "Our Blog",
    description: "Insights, updates, and practical healthcare billing content.",
    to: "/blog",
  },
  {
    label: "Case Studies",
    description: "Real-world examples of PSP RCM impact.",
    to: "/case-studies",
  },
  {
    label: "FAQs",
    description: "Answers to common questions about PSP RCM.",
    to: "/faqs",
  },
];

const solutionItems = [
  {
    label: "Our Services",
    description: "Service overview and billing support.",
    to: "/services",
  },
  {
    label: "Specialities",
    description: "Support tailored to specialty workflows.",
    to: "/specialities",
  },
  {
    label: "Digital Marketing",
    description: "Practice visibility and growth support.",
    to: "/digital-marketing",
  },
];

export default function Header() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [resourceOpen, setResourceOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setDrawerOpen(false);
    setResourceOpen(false);
    setSolutionsOpen(false);
  }, [location.pathname, location.hash]);

  const handleNavigationClick = (to) => {
    navigate(to);
  };

  return (
    <header className={styles.siteHeader}>
      <div className={`container ${styles.headerShell}`}>
        <NavLink
          to="/"
          className={styles.brand}
          aria-label="Physicians Success Partners home"
        >
          <img
            src="/logo/main-logo.webp"
            alt="Physicians Success Partners"
            className={styles.brandLogo}
          />
        </NavLink>

        <nav className={styles.desktopNav} aria-label="Primary navigation">
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `${styles.navLink} ${isActive ? styles.navLinkActive : ""}`
            }
          >
            Home
          </NavLink>
          <div
            className={`${styles.navDropdown} ${solutionsOpen ? styles.isOpen : ""}`}
            onMouseEnter={() => setSolutionsOpen(true)}
            onMouseLeave={() => setSolutionsOpen(false)}
          >
            <button
              type="button"
              className={styles.navTextButton}
              onClick={() => setSolutionsOpen((open) => !open)}
              aria-expanded={solutionsOpen}
            >
              Solutions
              <span className={styles.caret} aria-hidden="true" />
            </button>
            <div className={styles.dropdownPanel}>
              {solutionItems.map((item) => (
                <button
                  key={item.to}
                  type="button"
                  className={styles.dropdownLink}
                  onClick={() => handleNavigationClick(item.to)}
                >
                  <strong>{item.label}</strong>
                  <span>{item.description}</span>
                </button>
              ))}
            </div>
          </div>
          <NavLink
            to="/about-us"
            className={({ isActive }) =>
              `${styles.navLink} ${isActive ? styles.navLinkActive : ""}`
            }
          >
            About Us
          </NavLink>
          <div
            className={`${styles.navDropdown} ${resourceOpen ? styles.isOpen : ""}`}
            onMouseEnter={() => setResourceOpen(true)}
            onMouseLeave={() => setResourceOpen(false)}
          >
            <button
              type="button"
              className={styles.navTextButton}
              onClick={() => setResourceOpen((open) => !open)}
              aria-expanded={resourceOpen}
            >
              Resource
              <span className={styles.caret} aria-hidden="true" />
            </button>
            <div className={styles.dropdownPanel}>
              {resourceItems.map((item) => (
                <button
                  key={item.to}
                  type="button"
                  className={styles.dropdownLink}
                  onClick={() => handleNavigationClick(item.to)}
                >
                  <strong>{item.label}</strong>
                  <span>{item.description}</span>
                </button>
              ))}
            </div>
          </div>
        </nav>

        <div className={`${styles.headerActions} ${styles.desktopCta}`}>
          <a href={contactInfo.phoneHref} className={styles.headerPhone}>
            <span className={styles.headerPhoneIcon} aria-hidden="true">
              <FaPhoneAlt />
            </span>
            <span>{contactInfo.phoneDisplay}</span>
          </a>

          <NavLink to="/contact-us" className={styles.headerCta}>
            Let&apos;s Connect
          </NavLink>
        </div>

        <button
          type="button"
          className={`${styles.menuToggle} ${drawerOpen ? styles.isOpen : ""}`}
          onClick={() => setDrawerOpen((open) => !open)}
          aria-label="Toggle navigation menu"
          aria-expanded={drawerOpen}
        >
          <span />
          <span />
          <span />
        </button>
      </div>

      <div
        className={`${styles.mobileDrawer} ${drawerOpen ? styles.isOpen : ""}`}
      >
        <div className={`container ${styles.mobileDrawerInner}`}>
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `${styles.mobileLink} ${isActive ? styles.mobileLinkActive : ""}`
            }
          >
            Home
          </NavLink>
          <NavLink
            to="/about-us"
            className={({ isActive }) =>
              `${styles.mobileLink} ${isActive ? styles.mobileLinkActive : ""}`
            }
          >
            About Us
          </NavLink>
          <button
            type="button"
            className={`${styles.mobileMenuButton} ${resourceOpen ? styles.isOpen : ""}`}
            onClick={() => setResourceOpen((open) => !open)}
            aria-expanded={resourceOpen}
          >
            Resource
            <span className={styles.caret} aria-hidden="true" />
          </button>
          <div
            className={`${styles.mobileSubmenu} ${resourceOpen ? styles.isOpen : ""}`}
          >
            {resourceItems.map((item) => (
              <button
                key={item.to}
                type="button"
                className={styles.mobileSublink}
                onClick={() => handleNavigationClick(item.to)}
              >
                {item.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            className={`${styles.mobileMenuButton} ${solutionsOpen ? styles.isOpen : ""}`}
            onClick={() => setSolutionsOpen((open) => !open)}
            aria-expanded={solutionsOpen}
          >
            Solutions
            <span className={styles.caret} aria-hidden="true" />
          </button>
          <div
            className={`${styles.mobileSubmenu} ${solutionsOpen ? styles.isOpen : ""}`}
          >
            {solutionItems.map((item) => (
              <button
                key={item.to}
                type="button"
                className={styles.mobileSublink}
                onClick={() => handleNavigationClick(item.to)}
              >
                {item.label}
              </button>
            ))}
          </div>
          <a href={contactInfo.phoneHref} className={styles.mobileContact}>
            <FaPhoneAlt aria-hidden="true" />
            <span>{contactInfo.phoneDisplay}</span>
          </a>
          <NavLink
            to="/contact-us"
            className={`${styles.headerCta} ${styles.mobileCta}`}
          >
            Let&apos;s Connect
          </NavLink>
        </div>
      </div>
    </header>
  );
}
